import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  LockOpen as ForceReleaseIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  TaskAlt as CloseJobIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import {
  flushedNotice,
  fulfillmentApiBase,
  fulfillmentListPage,
  labelingJobsPath,
  labelingModeInfo,
  labelingOrderStateInfo,
  transferMoveErrors,
  transferMoveInfo,
  withAccountBody,
  withAccountParams,
} from '../../utils/fulfillmentApi.mjs';
import ConfirmDialog from './ConfirmDialog';
import FulfillmentStatusChip from './FulfillmentStatusChip';
import FulfillmentProductThumb from './FulfillmentProductThumb';
import B2bChip from './B2bChip';
import { isB2bTask } from '../../pages/fulfillmentUi.mjs';

const PAGE_SIZE_KEY = 'fulfillment_rows_per_page';
const TABLE_SX = {
  minWidth: 760,
  '& .MuiTableCell-body': { py: 0.625 },
  '& .MuiChip-root': { height: 22 },
  '& .MuiChip-label': { px: 0.875 },
};

const COLUMNS = [
  { key: '__view', label: '' },
  { key: 'code', label: 'Задание', sortable: true },
  { key: 'progress', label: 'Собрано / передано / проклеено' },
  { key: 'labeling', label: 'Проклейка' },
  { key: 'assigned_to', label: 'Сборщик', sortable: true },
  { key: 'status', label: 'Статус', sortable: true },
  { key: '__actions', label: '', align: 'right' },
];

function initialPageSize() {
  const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
  return [25, 50, 100, 200].includes(saved) ? saved : 50;
}

function errorText(error) {
  return error?.response?.data?.message || error.message || 'Не удалось загрузить задания';
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

/** Закрытие доступно для new/in_progress, в т.ч. с 0 подобранных позиций. */
function canCloseJob(job) {
  return job && !['completed', 'cancelled'].includes(job.status);
}

function needsMoveRetry(job) {
  if (transferMoveErrors(job?.transfers).length) return true;
  return Boolean(job?.ms_move_required && job?.ms_move_status === 'error' && job?.ms_move_error);
}

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

const TRANSFER_KIND_LABEL = { partial: 'Частичная', final: 'При закрытии', legacy: 'До партий' };

function transferStatusText(transfer) {
  if (!transfer?.ms_move_required && !transfer?.ms_move_status) return 'Не требуется';
  if (transfer.ms_move_status === 'done') return 'Проведено';
  if (transfer.ms_move_status === 'error') return 'Ошибка';
  if (transfer.ms_move_status === 'in_progress') return 'Выполняется';
  return '—';
}

export default function PickingJobsPanel({
  accountId,
  marketplace = 'wb',
  canManage,
  refreshKey = 0,
  onChanged,
}) {
  const isOzon = marketplace === 'ozon';
  const canAssign = marketplace === 'wb';
  const apiBase = fulfillmentApiBase(marketplace);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiBase}/picking-jobs`, {
        params: withAccountParams(marketplace, accountId, {
          page: fulfillmentListPage(marketplace, page),
          page_size: rowsPerPage,
          sort_key: sort.key,
          sort_dir: sort.dir,
        }),
      });
      setJobs(data?.items || []);
      setTotal(Number(data?.total) || 0);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, [accountId, apiBase, marketplace, page, rowsPerPage, sort]);

  useEffect(() => { setPage(0); }, [accountId, marketplace]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    // Список исполнителей нужен для create/assign; assign UI остаётся WB-only ниже.
    if (!canManage) {
      setAssignees([]);
      return undefined;
    }
    let cancelled = false;
    axios.get(`${apiBase}/picking-jobs/assignees`, {
      params: {
        account_id: accountId || undefined,
        marketplace: marketplace || undefined,
      },
      silent: true,
    })
      .then(({ data }) => {
        if (!cancelled) setAssignees(data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setAssignees([]);
      });
    return () => { cancelled = true; };
  }, [apiBase, canManage, accountId, marketplace]);

  const handleSort = (key) => {
    setPage(0);
    setSort((old) => ({
      key,
      dir: old.key === key && old.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const jobDetailUrl = (id) => `${apiBase}/picking-jobs/${id}`;

  const openDetail = async (job) => {
    setDetail(job);
    setDetailLoading(true);
    setError('');
    try {
      const { data } = await axios.get(jobDetailUrl(job.id), {
        params: withAccountParams(marketplace, accountId),
      });
      setDetail(data);
    } catch (requestError) {
      setError(errorText(requestError));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const cancel = async (job) => {
    setAction(`cancel:${job.id}`);
    try {
      await axios.post(
        `${apiBase}/picking-jobs/${job.id}/cancel`,
        withAccountBody(marketplace, accountId),
      );
      setNotice('Задание отменено');
      await load();
      onChanged?.();
      setDetail(null);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setAction('');
    }
  };

  const closeJob = async (job, { retry = false } = {}) => {
    setAction(`close:${job.id}`);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${job.id}/close`,
        withAccountBody(marketplace, accountId),
      );
      setNotice(retry
        ? (data?.ms_move_id
          ? `Перемещение проведено, задание ${data.code || job.code || ''} закрыто`
          : 'Задание закрыто')
        : `Задание ${data?.code || job.code || ''} закрыто`);
      await load();
      onChanged?.();
      if (detail && String(detail.id) === String(job.id)) {
        const refreshed = await axios.get(jobDetailUrl(job.id), {
          params: withAccountParams(marketplace, accountId),
        });
        setDetail(refreshed.data);
      }
    } catch (requestError) {
      setError(errorText(requestError));
      if (detail && String(detail.id) === String(job.id)) {
        try {
          const refreshed = await axios.get(jobDetailUrl(job.id), {
            params: withAccountParams(marketplace, accountId),
          });
          setDetail(refreshed.data);
        } catch (_) { /* ignore */ }
      }
      await load();
    } finally {
      setAction('');
    }
  };

  const refreshDetailIfOpen = async (jobId) => {
    if (!detail || String(detail.id) !== String(jobId)) return;
    try {
      const refreshed = await axios.get(jobDetailUrl(jobId), {
        params: withAccountParams(marketplace, accountId),
      });
      setDetail(refreshed.data);
    } catch (_) { /* ignore */ }
  };

  const retryTransferMove = async (job, transfer) => {
    setAction(`retry:${job.id}:${transfer.id}`);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${job.id}/transfers/${transfer.id}/retry-move`,
        withAccountBody(marketplace, accountId),
      );
      setNotice(`Перемещение партии №${transfer.seq_no} задания ${data?.code || job.code || ''} проведено`);
      if (detail && String(detail.id) === String(job.id) && data?.id) setDetail(data);
      onChanged?.();
    } catch (requestError) {
      setError(errorText(requestError));
      await refreshDetailIfOpen(job.id);
    } finally {
      setAction('');
      await load();
    }
  };

  /** Повтор move: по партии с ошибкой; без партий в ответе — прежний повтор закрытия. */
  const retryMove = (job) => {
    const failed = transferMoveErrors(job?.transfers)[0];
    if (failed) {
      retryTransferMove(job, failed);
      return;
    }
    askCloseJob(job, { retry: true });
  };

  const forceRelease = async (job) => {
    setAction(`release:${job.id}`);
    setError('');
    try {
      const { data } = await axios.post(
        labelingJobsPath(job.id, 'release'),
        withAccountBody(marketplace, accountId, { force: true }),
      );
      const flushedText = flushedNotice(data?.flushed);
      setNotice([`Задание ${job.code || job.id} снято с проклейки`, flushedText].filter(Boolean).join('. '));
      await refreshDetailIfOpen(job.id);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setAction('');
      await load();
    }
  };

  const askForceRelease = (job) => {
    const who = job?.labeling?.locked_by_name || 'сотрудника';
    setConfirm({
      title: 'Снять с проклейки',
      message: `Снять задание ${job?.code || job?.id} с ${who}? Проверенные товары «в руках» лягут на палету задания, `
        + 'задание станет доступно другим.',
      confirmText: 'Снять',
      onConfirm: () => {
        setConfirm(null);
        forceRelease(job);
      },
    });
  };

  const unpickedCount = (job) => {
    const totalItems = Number(job?.items_total);
    const picked = Number(job?.items_picked);
    if (Number.isFinite(totalItems) && Number.isFinite(picked)) {
      return Math.max(0, totalItems - picked);
    }
    if (Array.isArray(job?.items)) {
      return job.items.filter((item) => item.status !== 'picked').length;
    }
    return 0;
  };

  const askCancelJob = (job) => {
    const code = job?.code || job?.id || '';
    setConfirm({
      title: 'Отменить задание',
      message: `Отменить задание ${code}? Все заказы вернутся в «Ожидают сборки».`,
      confirmText: 'Отменить задание',
      onConfirm: () => {
        setConfirm(null);
        cancel(job);
      },
    });
  };

  const askCloseJob = (job, options) => {
    const code = job?.code || job?.id || '';
    const left = unpickedCount(job);
    const picked = Number(job?.items_picked) || 0;
    const transferred = Number(job?.items_transferred) || 0;
    const rest = Math.max(0, picked - transferred);
    const message = `Закрыть задание ${code}: передать остаток (${rest}) на проклейку, `
      + `вернуть ${left} несобранных в «Ожидают сборки».`;
    setConfirm({
      title: 'Закрыть задание',
      message,
      confirmText: 'Закрыть',
      onConfirm: () => {
        setConfirm(null);
        closeJob(job, options);
      },
    });
  };

  const assign = async (assignedTo) => {
    if (!detail || marketplace !== 'wb') return;
    setAssigning(true);
    try {
      const { data } = await axios.patch(
        `${apiBase}/picking-jobs/${detail.id}/assign`,
        withAccountBody(marketplace, accountId || detail.account_id, {
          assigned_to: Number(assignedTo),
        }),
      );
      setDetail((old) => ({ ...old, assigned_to: data.assigned_to, assignee: data.assignee }));
      await load();
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}

      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Обновить
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)' }}>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column.key} align={column.align || 'left'}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={sort.key === column.key}
                        direction={sort.key === column.key ? sort.dir : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const totalItems = Number(job.items_total) || 0;
                const picked = Number(job.items_picked) || 0;
                const transferred = Number(job.items_transferred) || 0;
                const labeled = Number(job.items_labeled) || 0;
                const progress = totalItems ? Math.round((picked / totalItems) * 100) : 0;
                const labeledProgress = totalItems ? Math.round((labeled / totalItems) * 100) : 0;
                const closing = action === `close:${job.id}` || String(action).startsWith(`retry:${job.id}:`);
                const retry = needsMoveRetry(job);
                const labeling = job.labeling || {};
                const releasing = action === `release:${job.id}`;
                return (
                  <TableRow key={job.id} hover>
                    <TableCell sx={{ width: 44, pr: 0 }}>
                      <Tooltip title="Открыть задание">
                        <IconButton
                          size="small"
                          aria-label="Открыть задание"
                          onClick={() => openDetail(job)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="titleMedium" fontWeight={700}>
                        {job.code || job.name || `ID ${job.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="p">
                        {job.code ? (job.name || `ID ${job.id}`) : `ID ${job.id}`}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={labelingModeInfo(job.labeling_mode).color}
                        label={labelingModeInfo(job.labeling_mode).label}
                        sx={{ mt: 0.5, height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        {picked} / {transferred} / {labeled}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}из {totalItems}
                        </Typography>
                      </Typography>
                      <LinearProgress
                        variant="buffer"
                        value={labeledProgress}
                        valueBuffer={progress}
                        sx={{ mt: 0.75, height: 6, borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      {labeling.done_at ? (
                        <Typography variant="bodyMedium" color="success.main" fontWeight={700}>
                          Проклеено
                        </Typography>
                      ) : labeling.locked_by ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="bodyMedium" fontWeight={700}>
                            {labeling.locked_by_name || `ID ${labeling.locked_by}`}
                            {labeling.taken_at ? ` · ${formatTime(labeling.taken_at)}` : ''}
                          </Typography>
                          {canManage && (
                            <Tooltip title="Снять с проклейки">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  aria-label="Снять с проклейки"
                                  disabled={Boolean(action)}
                                  onClick={() => askForceRelease(job)}
                                >
                                  {releasing
                                    ? <CircularProgress size={16} color="inherit" />
                                    : <ForceReleaseIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="bodySmall" color="text.secondary">
                          {transferred > labeled ? 'Ожидает' : '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        {job.assignee?.name || 'Не назначен'}
                      </Typography>
                      {job.created_at && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Создано: {formatDate(job.created_at)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <FulfillmentStatusChip kind="pickingJob" status={job.status} />
                      {retry && (
                        <Typography variant="caption" color="warning.main" component="p" sx={{ mt: 0.5 }}>
                          Ошибка перемещения МС
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {canManage && (canCloseJob(job) || retry) && (
                        <Tooltip title={retry ? 'Повторить перемещение' : 'Закрыть задание'}>
                          <span>
                            <IconButton
                              color={retry ? 'warning' : 'primary'}
                              disabled={Boolean(action)}
                              onClick={() => (retry ? retryMove(job) : askCloseJob(job))}
                              aria-label={retry ? 'Повторить перемещение' : 'Закрыть задание'}
                            >
                              {closing
                                ? <CircularProgress size={20} color="inherit" />
                                : (retry ? <ReplayIcon /> : <CloseJobIcon />)}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!jobs.length && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                    Заданий пока нет. Создайте задание из выбранных заказов на вкладке «Ожидают сборки».
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_event, value) => setPage(value)}
          onRowsPerPageChange={(event) => {
            const value = Number(event.target.value);
            localStorage.setItem(PAGE_SIZE_KEY, String(value));
            setRowsPerPage(value);
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        />
      </Paper>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack spacing={0.5}>
            <Typography variant="titleLarge">{detail?.code || detail?.name || 'Задание на подбор'}</Typography>
            {detail?.code && (
              <Typography variant="bodySmall" color="text.secondary">{detail.name}</Typography>
            )}
            {detail && (
              <Chip
                size="small"
                variant="outlined"
                color={labelingModeInfo(detail.labeling_mode).color}
                label={labelingModeInfo(detail.labeling_mode).label}
                sx={{ alignSelf: 'flex-start' }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {needsMoveRetry(detail) && !(detail?.transfers || []).length && (
                <Alert severity="error">
                  Перемещение МойСклад не выполнено: {detail.ms_move_error}. Можно безопасно повторить закрытие —
                  повторное перемещение не создастся.
                </Alert>
              )}
              {transferMoveErrors(detail?.transfers).length > 0 && (
                <Alert severity="error">
                  Перемещение МойСклад по партии не выполнено — её заказы не попадут на проклейку, пока не повторите.
                  Повтор безопасен: второй документ не создастся.
                </Alert>
              )}
              {!(detail?.transfers || []).length && detail?.ms_move_status === 'done' && detail?.ms_move_name && (
                <Alert severity="success">Перемещение МС: {detail.ms_move_name}</Alert>
              )}
              {detail?.labeling?.locked_by && !detail?.labeling?.done_at && (
                <Alert
                  severity="info"
                  action={canManage ? (
                    <Button
                      color="inherit"
                      size="small"
                      disabled={Boolean(action)}
                      onClick={() => askForceRelease(detail)}
                    >
                      Снять с проклейки
                    </Button>
                  ) : null}
                >
                  На проклейке: {detail.labeling.locked_by_name || `ID ${detail.labeling.locked_by}`}
                  {detail.labeling.taken_at ? ` с ${formatTime(detail.labeling.taken_at)}` : ''}
                </Alert>
              )}
              {(detail?.transfers || []).length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ '& .MuiChip-root': { height: 22 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Партия</TableCell>
                        <TableCell>Позиций</TableCell>
                        <TableCell>Перемещение МС</TableCell>
                        <TableCell>Документ</TableCell>
                        <TableCell>Передана</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.transfers.map((transfer) => {
                        const info = transferMoveInfo(transfer);
                        const retrying = action === `retry:${detail.id}:${transfer.id}`;
                        return (
                          <TableRow key={transfer.id}>
                            <TableCell>
                              <Typography variant="bodyMedium" fontWeight={700}>№{transfer.seq_no}</Typography>
                              <Typography variant="caption" color="text.secondary" component="p">
                                {TRANSFER_KIND_LABEL[transfer.kind] || transfer.kind || ''}
                              </Typography>
                            </TableCell>
                            <TableCell>{Number(transfer.items_count) || 0}</TableCell>
                            <TableCell>
                              <Chip size="small" color={info.color} label={transferStatusText(transfer)} />
                              {transfer.ms_move_error && (
                                <Typography variant="caption" color="error.main" component="p" sx={{ mt: 0.5 }}>
                                  {transfer.ms_move_error}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{transfer.ms_move_name || '—'}</TableCell>
                            <TableCell>{formatDate(transfer.created_at)}</TableCell>
                            <TableCell align="right">
                              {canManage && transfer.ms_move_status === 'error' && (
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  disabled={Boolean(action)}
                                  onClick={() => retryTransferMove(detail, transfer)}
                                >
                                  {retrying ? <CircularProgress size={16} color="inherit" /> : 'Повторить перемещение'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {canManage && canAssign && detail && !['completed', 'cancelled'].includes(detail.status) && (
                <FormControl fullWidth size="small">
                  <InputLabel>Назначенный сборщик</InputLabel>
                  <Select
                    value={detail.assigned_to || ''}
                    label="Назначенный сборщик"
                    disabled={assigning}
                    onChange={(event) => assign(event.target.value)}
                  >
                    {assignees.map((assignee) => (
                      <MenuItem key={assignee.id} value={assignee.id}>{assignee.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(detail?.items || []).map((item) => {
                const article = item.product?.article
                  || item.task?.article
                  || item.posting?.offer_id
                  || item.posting?.sku
                  || (item.task?.nm_id ? `nmID ${item.task.nm_id}` : null)
                  || '—';
                const orderLabel = item.posting?.yandex_order_id
                  || item.yandex_order_id
                  || item.posting?.posting_number
                  || item.task?.wb_order_id
                  || item.posting_number
                  || '—';
                const barcode = item.posting?.barcode || item.task?.barcode || '—';
                return (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} alignItems={{ sm: 'center' }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        <FulfillmentProductThumb
                          product={item.product}
                          task={item.task || item.posting}
                          size={48}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="bodyMedium" fontWeight={700}>
                            {article}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="p">
                            {isOzon ? 'Отправление' : 'Заказ'}
                            {' '}
                            {orderLabel}
                            {' · ШК '}
                            {barcode}
                            {marketplace === 'wb' && item.task?.nm_id ? ` · nmID ${item.task.nm_id}` : ''}
                          </Typography>
                          <B2bChip show={marketplace === 'wb' && isB2bTask(item.task)} sx={{ mt: 0.375 }} />
                          {item.stock && (
                            <Typography variant="caption" color="text.secondary" component="p">
                              МС {item.stock.ms_stock ?? '—'} · резерв {item.stock.reserved_total ?? 0} · доступно {item.stock.available ?? '—'}
                            </Typography>
                          )}
                          {item.posting?.composition_alert && (
                            <Typography variant="caption" color="warning.main" component="p">
                              Изменился состав отправления
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                        {item.transfer_id != null ? (
                          <Chip size="small" color="secondary" label="Передано" />
                        ) : (
                          <FulfillmentStatusChip
                            code={item.status === 'picked' ? 'picked' : 'pending'}
                            label={item.status === 'picked' ? 'Подобран' : 'Ожидает'}
                          />
                        )}
                        {item.labeling_state && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={labelingOrderStateInfo(item.labeling_state, detail?.labeling_mode).color}
                            label={labelingOrderStateInfo(item.labeling_state, detail?.labeling_mode).label}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {canManage && detail?.status === 'new' && (
            <Button color="error" onClick={() => askCancelJob(detail)} disabled={Boolean(action)}>
              Отменить задание
            </Button>
          )}
          {canManage && canCloseJob(detail) && !transferMoveErrors(detail?.transfers).length && (
            <Button
              color={needsMoveRetry(detail) ? 'warning' : 'primary'}
              variant="contained"
              disabled={Boolean(action)}
              onClick={() => askCloseJob(detail, { retry: needsMoveRetry(detail) })}
            >
              {action === `close:${detail?.id}`
                ? <CircularProgress size={18} color="inherit" />
                : (needsMoveRetry(detail) ? 'Повторить закрытие' : 'Закрыть задание')}
            </Button>
          )}
          <Button onClick={() => setDetail(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
      />
    </Stack>
  );
}
